import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { PositionChangeModal } from 'components/PositionChangeModal';
import useEmployeePosition from 'hooks/useEmployeePosition';
import { getEmployeeId, isAdmin } from 'utils/auth';
import { useNotifications } from 'contexts/notification-context';

const employeeType = PropTypes.shape({
    id: PropTypes.string.isRequired,
    position: PropTypes.string,
    positionVersion: PropTypes.number,
    active: PropTypes.bool,
}).isRequired;

const displayDate = (value) => {
    const date = new Date(value);
    return value && !Number.isNaN(date.getTime()) ? date.toLocaleString() : 'Date unavailable';
};

// Key identity/privilege changes to discard any open form and requests from the previous session.
export const EmployeePositionSection = ({ employee }) => {
    const viewerId = getEmployeeId();
    const own = !!viewerId && employee.id === viewerId;
    const direct = own && isAdmin();
    return <PositionDetails key={`${employee.id}:${viewerId}:${direct}`}
        initialEmployee={employee} viewerId={viewerId || ''} own={own} direct={direct} />;
};

EmployeePositionSection.propTypes = { employee: employeeType };

const PositionDetails = ({ initialEmployee, viewerId, own, direct }) => {
    const { refreshPositionRequests } = useNotifications();
    const titleId = useId();
    const [editing, setEditing] = useState(null);
    const [message, setMessage] = useState('');
    const { data, status, actions } = useEmployeePosition({
        initialEmployee,
        employeeId: initialEmployee.id,
        viewerId,
        enabled: own,
        onPositionRequestsChanged: refreshPositionRequests,
    });
    const { employee, requests } = data;
    const { pending, latestResolved } = requests;
    const { loading, saving, syncError, mutationError } = status;
    const unavailable = loading || saving || !!syncError || employee.active === false;

    const openEditor = () => {
        actions.clearMutationError();
        setMessage('');
        // Keep the version from when the dialog opened, even if a later read refreshes the section.
        setEditing({ position: employee.position, positionVersion: employee.positionVersion });
    };

    const save = async (position) => {
        setMessage('');
        const result = direct
            ? await actions.updatePosition(position, editing.positionVersion)
            : await actions.submitRequest(position);
        if (result.ok) {
            setEditing(null);
            setMessage(direct ? 'Position updated.' : 'Position change request submitted.');
        }
    };

    const cancel = async () => {
        const result = await actions.cancelRequest(pending.id, pending.version);
        if (result.ok) {
            setMessage('Position change request cancelled.');
        }
    };

    return (
        <Box component="section" aria-labelledby={titleId} sx={{ mt: 3, mb: 2 }}>
            <Typography id={titleId} variant="h6" component="h2">Position</Typography>
            <Typography sx={{ mt: 1 }}>Current position: {(own ? employee.position : initialEmployee.position) || 'Not set'}</Typography>
            {own && <Stack spacing={2} sx={{ mt: 2 }}>
                {loading && <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} /> Refreshing position…
                </Box>}
                {syncError && <Alert severity="error" action={
                    <Button color="inherit" onClick={actions.refresh} disabled={loading || saving}>Retry</Button>
                }>{syncError}</Alert>}
                {mutationError && !editing && <Alert severity="error">{mutationError}</Alert>}
                {message && <Alert severity="success">{message}</Alert>}
                {pending && <Alert severity="info">
                    <Typography>Pending request: {pending.requestedPosition}</Typography>
                    <Typography variant="body2">Submitted: {displayDate(pending.requestedAtUtc)}</Typography>
                    <Button onClick={cancel} disabled={unavailable} sx={{ mt: 1 }}>Cancel request</Button>
                </Alert>}
                {latestResolved && <Alert severity={latestResolved.status === 'Approved' ? 'success' :
                    latestResolved.status === 'Rejected' ? 'warning' : 'info'}>
                    <Typography>Latest request: {latestResolved.status} — {latestResolved.requestedPosition}</Typography>
                    {latestResolved.resolvedAtUtc && <Typography variant="body2">
                        Resolved: {displayDate(latestResolved.resolvedAtUtc)}
                    </Typography>}
                    {latestResolved.resolutionReason && <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                        {latestResolved.resolutionReason}
                    </Typography>}
                </Alert>}
                {employee.active === false ? <Typography variant="body2">
                    Position changes are available to active employees.
                </Typography> : <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    <Button variant="contained" onClick={openEditor}
                        disabled={unavailable || (!direct && !!pending)}>
                        {direct ? 'Edit position' : 'Request position change'}
                    </Button>
                    <Button onClick={actions.refresh} disabled={loading || saving}>Refresh status</Button>
                </Stack>}
            </Stack>}
            {own && editing && <PositionChangeModal mode={direct ? 'direct' : 'request'}
                currentPosition={editing.position} saving={saving} error={mutationError}
                disabled={unavailable || (!direct && !!pending)}
                onClose={() => { setEditing(null); actions.clearMutationError(); }} onSubmit={save} />}
        </Box>
    );
};

PositionDetails.propTypes = {
    initialEmployee: employeeType,
    viewerId: PropTypes.string.isRequired,
    own: PropTypes.bool.isRequired,
    direct: PropTypes.bool.isRequired,
};
